import { toMoney } from '@sequencehq/utils'
import {
  Text,
  MagicTable,
  MagicTableCell,
  MagicTableEmptyState,
  MagicTableFilterConfig,
  MagicTableFilterEmptyState,
  RowSpanningRule,
  useLinkMagicTableWithSearchParams,
  useMagicTableInfiniteQuery,
  useMultiSelectAsync,
  Icon3dBox,
  IconReceiptBill,
  IconProjects,
  IconReceiptCredit,
  Box,
  Flex
} from '@sequencehq/core-components'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { useState } from 'react'
import { useSelectedCurrency } from '../../utils/useSelectedCurrency'
import { useSelectedDateRange } from '../../utils/useSelectedDateRange'
import { format } from '@sequencehq/utils/dates'
import { Currency } from '@sequencehq/api/utils/commonEnums'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import { humanize } from 'inflection'
import { RevRecDashboardDateFilter } from './RevRecDashboardDateFilter'
import { RevRecDashboardCurrencyFilter } from './RevRecDashboardCurrencyFilter'
import { CellContext } from '@tanstack/react-table'
import { transformJournalData } from './transformJournalData'
import { JournalDetailOverviewDrawer } from './JournalDetailOverviewDrawer'
import {
  formatPeriodMonthLabel,
  getPeriodMonthKey,
  isSamePeriod
} from 'RevenueRecognition/view/utils/isSamePeriod'
import { InvoiceLinkCell } from 'RevenueRecognition/view/common/InvoiceLinkCell'
import { ProductLinkCell } from 'RevenueRecognition/view/common/ProductLinkCell'
import { customerIdFilterConfig } from 'Customer/index'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { RevRecDashboardStatsCard } from './RevRecDashboardStatsCard'
import { ErrorBoundary } from 'common/components/ErrorBoundary/ErrorBoundary'
import { CreditNoteLinkCell } from 'RevenueRecognition/view/common/CreditNoteLinkCell'
import PageError from 'components/ErrorPages/PageError'
import { getApiSorting } from 'RevenueRecognition/utils/sortingUtils'
import { Journal } from 'RevenueRecognition/revenueRecognition.types'
import { useLogEvent } from 'lib/postHog/useLogEvent'

export type RevRecTableJournalEntry = {
  id: string
  journalId: string
  period: string
  customer?: {
    id: string
    name: string
  }
  product?: {
    id: string
    name: string
  }
  invoice?: {
    id: string
    name: string
  }
  creditNote?: {
    id: string
    name: string
  }
  journalType?: 'MANUAL' | 'SYSTEM'
  ledgerAccount: {
    name: string
    debit?: number
    credit?: number
  }
}

type JournalFilters = {
  currency?: string[]
  dateFrom?: string
  dateTo?: string
  customerId?: string[]
  productId?: string[]
  invoiceId?: string[]
}

type JournalView = {
  model: RevRecTableJournalEntry
  controlFilters: JournalFilters
  propFilters: Record<string, unknown>
}

export type RevRecTableGroupBy = 'customer' | 'product' | 'date' | undefined

const columnOrderByGroup: Record<string, string[]> = {
  customer: [
    'customer',
    'date',
    'product',
    'invoice',
    'creditNote',
    'ledger',
    'debit',
    'credit'
  ],
  product: [
    'product',
    'date',
    'customer',
    'invoice',
    'creditNote',
    'ledger',
    'debit',
    'credit'
  ],
  date: [
    'date',
    'customer',
    'product',
    'invoice',
    'creditNote',
    'ledger',
    'debit',
    'credit'
  ]
}

const getOrderedColumns = (
  selectedCurrency: Currency | null,
  groupBy: RevRecTableGroupBy
) => {
  const baseColumns = [
    {
      id: 'date',
      header: 'Period',
      accessorFn: (row: RevRecTableJournalEntry) =>
        formatPeriodMonthLabel(row.period),
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => (
        <MagicTableCell
          text={value.getValue<string>()}
          textAlign={value.column.columnDef?.meta?.textAlign}
        />
      ),
      enableSorting: true
    },
    {
      id: 'customer',
      header: 'Customer',
      accessorFn: (row: RevRecTableJournalEntry) => row.customer?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const customer = value.row.original.customer
        if (!customer) {
          return null
        }
        return (
          <Flex
            alignItems="center"
            justifyContent="space-between"
            height="40px"
            pl="6px"
          >
            <CustomerPreviewCardPill customerId={customer.id} variant="ghost" />
          </Flex>
        )
      },
      enableSorting: true
    },
    {
      id: 'product',
      header: 'Product',
      accessorFn: (row: RevRecTableJournalEntry) =>
        row.product?.name ?? 'Performance obligation',
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const product = value.row.original.product

        return <ProductLinkCell product={product} />
      },
      enableSorting: true
    },
    {
      id: 'invoice',
      header: 'Invoice',
      accessorFn: (row: RevRecTableJournalEntry) => row.invoice?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const invoice = value.row.original.invoice

        return <InvoiceLinkCell invoice={invoice} />
      }
    },
    {
      id: 'creditNote',
      header: 'Credit note',
      accessorFn: (row: RevRecTableJournalEntry) => row.creditNote?.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const creditNote = value.row.original.creditNote

        return <CreditNoteLinkCell creditNote={creditNote} />
      }
    },
    {
      id: 'ledger',
      header: 'Ledger',
      accessorFn: (row: RevRecTableJournalEntry) => row.ledgerAccount.name,
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => (
        <MagicTableCell
          text={humanize(value.getValue<string>())}
          data-testid="revrec.journals.table.ledger"
        />
      )
    },
    {
      id: 'debit',
      header: 'Debit',
      accessorFn: (row: RevRecTableJournalEntry) => {
        const totalDebit = row.ledgerAccount.debit
        return {
          currencyCode: selectedCurrency as Currency,
          debitValue: totalDebit
        }
      },
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const { currencyCode, debitValue } = value.getValue<{
          currencyCode: Currency
          debitValue: number
        }>()

        return (
          <MagicTableCell
            text={toMoney({
              currency: currencyCode,
              value: debitValue?.toString()
            })}
            textAlign="right"
          />
        )
      },
      meta: { textAlign: 'right' as const }
    },
    {
      id: 'credit',
      header: 'Credit',
      accessorFn: (row: RevRecTableJournalEntry) => {
        const totalCredit = row.ledgerAccount.credit
        return {
          currencyCode: selectedCurrency as Currency,
          creditValue: totalCredit
        }
      },
      cell: (value: CellContext<RevRecTableJournalEntry, unknown>) => {
        const { currencyCode, creditValue } = value.getValue<{
          currencyCode: Currency
          creditValue: number
        }>()

        return (
          <MagicTableCell
            text={toMoney({
              currency: currencyCode,
              value: creditValue?.toString()
            })}
            textAlign="right"
          />
        )
      },
      meta: { textAlign: 'right' as const }
    }
  ]

  if (!groupBy) {
    return baseColumns
  }

  const currentOrder = columnOrderByGroup[groupBy]
  return baseColumns.sort((a, b) => {
    const aIndex = currentOrder.indexOf(a.id)
    const bIndex = currentOrder.indexOf(b.id)
    return aIndex - bIndex
  })
}

const useRevRecDashboardTableFilters = () => {
  const { data: cachedInvoicesData } = useQuery(
    dashboardv99990101Client.getInvoices,
    {
      search: '',
      limit: 20
    },
    {
      select: data =>
        data?.items?.filter((invoice: any) => invoice.invoiceNumber) ?? []
    }
  )

  const { data: cachedProductsData } = useQuery(
    dashboardv99990101Client.getProducts,
    {
      name: '',
      limit: 20
    },
    {
      select: data => data?.items ?? []
    }
  )

  const { data: cachedCreditNotesData } = useQuery(
    dashboardv99990101Client.getCreditNotes,
    {
      limit: 100
    },
    {
      select: data =>
        data?.items?.filter((creditNote: any) => creditNote.creditNoteNumber) ??
        []
    }
  )

  // Get current filter values from URL for rehydration
  const [searchParams] = useSearchParams()
  const currentInvoiceIds = searchParams.getAll('invoiceId')
  const currentProductIds = searchParams.getAll('productId')
  const currentCreditNoteIds = searchParams.getAll('creditNoteId')

  const invoiceFilter = useMultiSelectAsync({
    paramName: 'invoiceId' as keyof JournalFilters,
    label: 'Invoice',
    icon: IconReceiptBill,
    cachedOptions: cachedInvoicesData?.map(invoice => ({
      value: invoice.id,
      label: invoice.invoiceNumber || invoice.id
    })),
    searchFetcher: async (term: string) => {
      const response = await dashboardv99990101Client.getInvoices({
        search: term,
        limit: 20
      })
      return (
        response.data?.items
          ?.filter((invoice: any) => invoice.invoiceNumber)
          .map(invoice => ({
            value: invoice.id,
            label: invoice.invoiceNumber || invoice.id
          })) ?? []
      )
    },
    entityFetcher: async (id: string) => {
      const response = await dashboardv99990101Client.getInvoice({ id })
      return {
        value: response.data?.id || id,
        label: response.data?.invoiceNumber || response.data?.id || id
      }
    },
    activeFilterValues: currentInvoiceIds
  })

  const productFilter = useMultiSelectAsync({
    paramName: 'productId' as keyof JournalFilters,
    label: 'Product',
    icon: Icon3dBox,
    cachedOptions: cachedProductsData?.map(product => ({
      value: product.id,
      label: product.name
    })),
    searchFetcher: async (term: string) => {
      const response = await dashboardv99990101Client.getProducts({
        name: term,
        limit: 20
      })
      return (
        response.data?.items?.map(product => ({
          value: product.id,
          label: product.name
        })) ?? []
      )
    },
    entityFetcher: async (id: string) => {
      const response = await dashboardv99990101Client.getProduct({ id })
      return {
        value: response.data?.id || id,
        label: response.data?.name || id
      }
    },
    activeFilterValues: currentProductIds
  })

  const creditNoteFilter = useMultiSelectAsync({
    paramName: 'creditNoteId' as keyof JournalFilters,
    label: 'Credit note',
    icon: IconReceiptCredit,
    cachedOptions: cachedCreditNotesData?.map(creditNote => ({
      value: creditNote.id,
      label: creditNote.creditNoteNumber || creditNote.id
    })),
    searchFetcher: async (term: string) => {
      const response = await dashboardv99990101Client.getCreditNotes({
        searchCreditNoteNumber: term
      })
      return (
        response.data?.items?.map(creditNote => ({
          value: creditNote.id,
          label: creditNote.creditNoteNumber || creditNote.id
        })) ?? []
      )
    },
    entityFetcher: async (id: string) => {
      const response = await dashboardv99990101Client.getCreditNote({ id })
      return {
        value: response.data?.id || id,
        label: response.data?.creditNoteNumber || response.data?.id || id
      }
    },
    activeFilterValues: currentCreditNoteIds
  })

  const filtersConfig: MagicTableFilterConfig<JournalFilters>[] = [
    {
      ...customerIdFilterConfig,
      paramName: 'customerId'
    },
    invoiceFilter as MagicTableFilterConfig<JournalFilters>,
    productFilter as MagicTableFilterConfig<JournalFilters>,
    creditNoteFilter as MagicTableFilterConfig<JournalFilters>
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filtersConfig, [
      'startDate',
      'endDate',
      'currency'
    ])

  return {
    activeFilters,
    onChangeActiveFilters,
    sortBy,
    onChangeSortBy,
    filtersConfig
  }
}

export const RevRecDashboardTable = () => {
  const navigate = useNavigate()
  const { currency: selectedCurrency } = useSelectedCurrency()
  const { dateRange } = useSelectedDateRange()
  const [groupBy, setGroupBy] = useState<RevRecTableGroupBy>('date')
  const [selectedJournalId, setSelectedJournalId] = useState<string | null>(
    null
  )
  const { logEvent } = useLogEvent()

  const {
    activeFilters,
    onChangeActiveFilters,
    sortBy,
    onChangeSortBy,
    filtersConfig
  } = useRevRecDashboardTableFilters()

  const apiSortBy = getApiSorting(groupBy, sortBy)

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboardv99990101Client.getJournalsPaginated,
    filtersConfig,
    activeFilters,
    apiSortBy,
    100,
    100,
    {
      from: dateRange[0] ? format(dateRange[0], 'yyyy-MM-dd') : '',
      to: dateRange[1] ? format(dateRange[1], 'yyyy-MM-dd') : '',
      currency: selectedCurrency ?? 'USD'
    }
  )

  const columns = getOrderedColumns(selectedCurrency, groupBy)

  const rowSpanningRules: RowSpanningRule<RevRecTableJournalEntry>[] = [
    {
      columnId: 'date',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'date') {
          return isSamePeriod(row1.period, row2.period)
        }
        return (
          row1.journalId === row2.journalId &&
          isSamePeriod(row1.period, row2.period)
        )
      }
    },
    {
      columnId: 'product',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'product') {
          if (!row1.product?.id && !row2.product?.id) {
            return row1.journalId === row2.journalId
          }
          if (!row1.product?.id || !row2.product?.id) {
            return false
          }
          return row1.product?.id === row2.product?.id
        }

        return (
          row1.journalId === row2.journalId &&
          row1.product?.id === row2.product?.id
        )
      }
    },
    {
      columnId: 'customer',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        if (groupBy === 'customer') {
          return row1.customer?.id === row2.customer?.id
        }

        return (
          row1.journalId === row2.journalId &&
          row1.customer?.id === row2.customer?.id
        )
      }
    },
    {
      columnId: 'ledger',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.name === row2.ledgerAccount.name &&
          row1.invoice?.id === row2.invoice?.id
        )
      }
    },
    {
      columnId: 'invoice',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.journalId === row2.journalId &&
          row1.invoice?.id === row2.invoice?.id
        )
      }
    },
    {
      columnId: 'creditNote',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.journalId === row2.journalId &&
          row1.creditNote?.id === row2.creditNote?.id
        )
      }
    },
    {
      columnId: 'debit',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.debit === row2.ledgerAccount.debit
        )
      }
    },
    {
      columnId: 'credit',
      spanBy: (
        row1: RevRecTableJournalEntry,
        row2: RevRecTableJournalEntry
      ) => {
        return (
          row1.id === row2.id &&
          row1.ledgerAccount.credit === row2.ledgerAccount.credit
        )
      }
    }
  ]

  return (
    <Box position="relative" height="100%" data-testid="revrec.journals.table">
      <JournalDetailOverviewDrawer
        journalId={selectedJournalId}
        selectedCurrency={selectedCurrency}
        closeDrawer={() => setSelectedJournalId(null)}
      />
      <CurrentUserId>
        {userId => (
          <MagicTable<JournalView>
            key={`${groupBy}-${selectedCurrency}`}
            filters={filtersConfig}
            activeFilters={activeFilters}
            onChangeActiveFilters={onChangeActiveFilters}
            entityNamePlural="journals"
            sortBy={sortBy}
            onChangeSortBy={onChangeSortBy}
            columns={columns}
            additionalHeaderRows={
              <ErrorBoundary
                name="RevRecDashboardTable.statsCard"
                fallback={<PageError />}
              >
                <RevRecDashboardStatsCard
                  hasResults={
                    infiniteQuery.isPending ||
                    (infiniteQuery.data?.pages[0]?.items || []).length !== 0
                  }
                />
              </ErrorBoundary>
            }
            sequenceUserId={userId}
            infiniteQuery={
              infiniteQuery as unknown as {
                data?:
                  | {
                      pages: Array<{
                        items: readonly RevRecTableJournalEntry[]
                        pagination: {
                          after?: string
                          before?: string
                          totalResultSize?: number
                        }
                      }>
                    }
                  | null
                  | undefined
                isFetching: boolean
                hasNextPage: boolean
                isFetchingNextPage: boolean
                fetchNextPage: () => void
              }
            }
            onRowClick={row => {
              setSelectedJournalId(row.journalId)
            }}
            enableColumnReordering={false}
            additionalToolbarComponentsV3={{
              start: (
                <Flex gap="8px">
                  <RevRecDashboardDateFilter />
                  <RevRecDashboardCurrencyFilter />
                </Flex>
              )
            }}
            emptyStateUnfiltered={entityNamePlural => (
              <MagicTableEmptyState
                entityNamePlural={entityNamePlural}
                imageSrc="/img/empty/invoices.png"
                learnMoreText="Learn more about revenue recognition journals"
                learnMoreHref="https://docs.sequencehq.com/revenue-recognition"
              />
            )}
            emptyStateFiltered={(entityNamePlural, clearFilters) => (
              <MagicTableFilterEmptyState
                entityNamePlural={entityNamePlural}
                entityIcon={IconProjects}
                actionClearFilters={clearFilters}
                actionViewAll={() => navigate('/revenue-recognition')}
                variant="TABLE"
              />
            )}
            grouping={{
              onGroupBy: columnId => {
                const newGroupBy = columnId as RevRecTableGroupBy
                setGroupBy(newGroupBy)

                logEvent({
                  eventName: 'revrec_journal_report_grouping_changed',
                  metadata: { groupBy: newGroupBy ?? 'none' }
                })

                // Auto-sync sortBy when grouping changes
                if (newGroupBy) {
                  onChangeSortBy(`${newGroupBy}_asc`)
                } else {
                  onChangeSortBy(null)
                }
              },
              rowSpanningRules,
              groupableColumns: ['customer', 'product', 'date'],
              groupBy,
              transformGroupData: (
                rows: RevRecTableJournalEntry[],
                groupBy?: string
              ) => {
                return transformJournalData(
                  // Data that is coming in from infinite query is actually an array of journals
                  // we do however want the type for this to be the same as the row type.
                  // This all happens because the data coming from API is different than what we want to have as a view model.
                  // We can't do this transformation outside of MT because it has to encompass all the rows fetched
                  // and that is taken care of by infiniteQuery inside of MT :V
                  rows as unknown as Journal[],
                  groupBy as RevRecTableGroupBy,
                  sortBy ?? undefined
                )
              },
              // For every fetched journal, there are 2 entries so the results size is doubled
              resultSizeMultiplier: 2,
              highlightRowGroup: row => row.journalId,
              getGroupKey: row => {
                switch (groupBy) {
                  case 'customer':
                    return row.customer?.id ?? 'no-customer'
                  case 'product':
                    return row.product?.id ?? row.journalId
                  case 'date':
                    return getPeriodMonthKey(row.period)
                  default:
                    return row.id
                }
              },
              renderCollapsedRow: (_groupKey, groupData, _groupBy) => {
                // Aggregate data by ledger account
                const ledgerSummaries = groupData.reduce<
                  Record<
                    string,
                    {
                      name: string
                      debit: number
                      credit: number
                    }
                  >
                >((acc, entry) => {
                  const { ledgerAccount } = entry
                  const key = ledgerAccount.name

                  if (!acc[key]) {
                    acc[key] = {
                      name: ledgerAccount.name,
                      debit: 0,
                      credit: 0
                    }
                  }

                  acc[key].debit += ledgerAccount.debit || 0
                  acc[key].credit += ledgerAccount.credit || 0

                  return acc
                }, {})

                // Convert to array of RevRecTableJournalEntry objects
                const baseEntry = groupData[0] // Use first entry as template
                return Object.values(ledgerSummaries).map(summary => ({
                  ...baseEntry,
                  id: `${baseEntry.id}-${summary.name}`,
                  ledgerAccount: {
                    name: summary.name,
                    debit: summary.debit,
                    credit: summary.credit
                  }
                }))
              },
              // Configuration for collapsed row rendering
              collapsedVisibleColumns: ['ledger', 'debit', 'credit'],
              collapsedBorderedColumns: ['ledger', 'debit', 'credit']
            }}
          />
        )}
      </CurrentUserId>
    </Box>
  )
}
